import React from 'react'
import { ChevronDown } from 'lucide-react'
import type { ProcessedMonthRow } from '../../../utils/overdueInvoiceAging'
import type { PeriodType } from '../../../utils/overdueInvoiceAging'

const ALL_MONTHS = [
	{ value: 1, label: 'January' },
	{ value: 2, label: 'February' },
	{ value: 3, label: 'March' },
	{ value: 4, label: 'April' },
	{ value: 5, label: 'May' },
	{ value: 6, label: 'June' },
	{ value: 7, label: 'July' },
	{ value: 8, label: 'August' },
	{ value: 9, label: 'September' },
	{ value: 10, label: 'October' },
	{ value: 11, label: 'November' },
	{ value: 12, label: 'December' },
]

const selectClass =
	'text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer max-w-[11rem]'

const FilterSelect: React.FC<{
	label: string
	value: string
	onChange: (v: string) => void
	options: { value: string; label: string }[]
}> = ({ label, value, onChange, options }) => (
	<div className="flex items-center gap-1.5">
		<span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
			{label}
		</span>
		<select value={value} onChange={e => onChange(e.target.value)} className={selectClass}>
			{options.map(o => (
				<option key={o.value} value={o.value}>
					{o.label}
				</option>
			))}
		</select>
	</div>
)

const popoverBtnClass =
	'text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-1 min-h-[28px] max-w-[11rem]'

export interface FiltersBarProps {
	sourceRows: ProcessedMonthRow[]
	periodType: PeriodType
	onPeriodType: (p: PeriodType) => void
	selectedYears: number[]
	onToggleYear: (y: number) => void
	onClearYears: () => void
	selectedMonths: number[]
	onToggleMonth: (m: number) => void
	onClearMonths: () => void
	selectedQuarter: string
	onQuarter: (q: string) => void
	selectedHalfYear: string
	onHalfYear: (h: string) => void
	onClearAll: () => void
	yearOpen: boolean
	onYearOpen: (v: boolean) => void
	monthOpen: boolean
	onMonthOpen: (v: boolean) => void
	clientCountLabel: string
}

export const FiltersBar: React.FC<FiltersBarProps> = ({
	sourceRows,
	periodType,
	onPeriodType,
	selectedYears,
	onToggleYear,
	onClearYears,
	selectedMonths,
	onToggleMonth,
	onClearMonths,
	selectedQuarter,
	onQuarter,
	selectedHalfYear,
	onHalfYear,
	onClearAll,
	yearOpen,
	onYearOpen,
	monthOpen,
	onMonthOpen,
	clientCountLabel,
}) => {
	const availableYears = [
		...new Set(sourceRows.map(r => Number(r.invoice_year)).filter(y => !Number.isNaN(y))),
	].sort((a, b) => b - a)

	const monthsWithData = new Set<string>()
	sourceRows.forEach(item => {
		if (item.invoice_year != null && item.invoice_month != null) {
			monthsWithData.add(`${item.invoice_year}-${item.invoice_month}`)
		}
	})
	const hasYearFilter = selectedYears.length > 0
	const availableMonths = ALL_MONTHS.filter(month => {
		if (!hasYearFilter) {
			return [...monthsWithData].some(key => {
				const [, m] = key.split('-')
				return parseInt(m, 10) === month.value
			})
		}
		return selectedYears.some(yr => monthsWithData.has(`${yr}-${month.value}`))
	})

	const sep = <div className="h-4 w-px bg-slate-200 shrink-0" aria-hidden />

	return (
		<div className="flex items-center gap-2 flex-wrap justify-end">
			<FilterSelect
				label="Period"
				value={periodType}
				onChange={v => {
					onPeriodType(v as PeriodType)
					onClearMonths()
					onQuarter('all')
					onHalfYear('all')
				}}
				options={[
					{ value: 'monthly', label: 'Monthly' },
					{ value: 'quarterly', label: 'Quarterly' },
					{ value: 'half-yearly', label: 'Half-yearly' },
					{ value: 'yearly', label: 'Yearly' },
				]}
			/>

			{sep}

			<div className="flex items-center gap-1.5 relative">
				<span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
					Year
				</span>
				<button
					type="button"
					onClick={() => {
						onMonthOpen(false)
						onYearOpen(!yearOpen)
					}}
					className={popoverBtnClass}
				>
					<span className="truncate text-left flex-1 min-w-0">
						{selectedYears.length === 0
							? 'All years'
							: selectedYears.length === 1
								? String(selectedYears[0])
								: `${selectedYears.length} years`}
					</span>
					<ChevronDown className="w-3 h-3 shrink-0 text-slate-500" />
				</button>
				{yearOpen && (
					<>
						<div className="absolute z-50 top-full right-0 mt-1 min-w-[10rem] bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
							<label className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[12px]">
								<input
									type="checkbox"
									checked={selectedYears.length === 0}
									onChange={() => {
										onClearYears()
										onYearOpen(false)
									}}
								/>
								All years
							</label>
							{availableYears.map(y => (
								<label
									key={y}
									className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[12px]"
								>
									<input
										type="checkbox"
										checked={selectedYears.includes(y)}
										onChange={() => {
											onToggleYear(y)
											onClearMonths()
										}}
									/>
									{y}
								</label>
							))}
						</div>
						<button
							type="button"
							className="fixed inset-0 z-40 cursor-default bg-transparent"
							aria-hidden
							onClick={() => onYearOpen(false)}
						/>
					</>
				)}
			</div>

			{periodType === 'monthly' && (
				<>
					{sep}
					<div className="flex items-center gap-1.5 relative">
						<span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
							Month
						</span>
						<button
							type="button"
							onClick={() => {
								onYearOpen(false)
								onMonthOpen(!monthOpen)
							}}
							className={popoverBtnClass}
						>
							<span className="truncate text-left flex-1 min-w-0">
								{selectedMonths.length === 0
									? 'All months'
									: selectedMonths.length === 1
										? ALL_MONTHS.find(m => m.value === selectedMonths[0])?.label
										: `${selectedMonths.length} months`}
							</span>
							<ChevronDown className="w-3 h-3 shrink-0 text-slate-500" />
						</button>
						{monthOpen && (
							<>
								<div className="absolute z-50 top-full right-0 mt-1 min-w-[10rem] bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
									<label className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[12px]">
										<input
											type="checkbox"
											checked={selectedMonths.length === 0}
											onChange={() => {
												onClearMonths()
												onMonthOpen(false)
											}}
										/>
										All months
									</label>
									{availableMonths.map(month => (
										<label
											key={month.value}
											className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 cursor-pointer text-[12px]"
										>
											<input
												type="checkbox"
												checked={selectedMonths.includes(month.value)}
												onChange={() => onToggleMonth(month.value)}
											/>
											{month.label}
										</label>
									))}
								</div>
								<button
									type="button"
									className="fixed inset-0 z-40 cursor-default bg-transparent"
									aria-hidden
									onClick={() => onMonthOpen(false)}
								/>
							</>
						)}
					</div>
				</>
			)}

			{periodType === 'quarterly' && (
				<>
					{sep}
					<FilterSelect
						label="Quarter"
						value={selectedQuarter}
						onChange={onQuarter}
						options={[
							{ value: 'all', label: 'All quarters' },
							{ value: '1', label: 'Q1 (Jan–Mar)' },
							{ value: '2', label: 'Q2 (Apr–Jun)' },
							{ value: '3', label: 'Q3 (Jul–Sep)' },
							{ value: '4', label: 'Q4 (Oct–Dec)' },
						]}
					/>
				</>
			)}

			{periodType === 'half-yearly' && (
				<>
					{sep}
					<FilterSelect
						label="Half"
						value={selectedHalfYear}
						onChange={onHalfYear}
						options={[
							{ value: 'all', label: 'All' },
							{ value: '1', label: 'H1' },
							{ value: '2', label: 'H2' },
						]}
					/>
				</>
			)}

			{sep}

			<button
				type="button"
				onClick={onClearAll}
				className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-1 py-0.5 shrink-0"
			>
				Clear all
			</button>

			<span
				className="text-[10px] text-slate-500 max-w-[200px] truncate shrink-0 hidden xl:inline"
				title={clientCountLabel}
			>
				{clientCountLabel}
			</span>
		</div>
	)
}
